import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../navbar/navbar.jsx";
import Footer from "../footer/footer.jsx";
import AnimatedSection from "../components/AnimatedSection.jsx";
import { FaInstagram, FaLinkedin, FaGithub } from "react-icons/fa";
import { translations } from '../utils/translations.js';
import "../styles/mobileFormFix.css";

const Contacto = ({ language, setLanguage, isDarkMode, setIsDarkMode }) => {
  // Get translations for current language
  const t = translations[language || 'es'];

  // Use props directly (no local state needed since App.js manages global state)
  const darkMode = isDarkMode || false;
  const currentLanguage = language || 'es';
  // Estado para el formulario
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar errores del campo cuando se empieza a escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Extraer datos del formulario
    const { firstName, lastName, email, message } = formData;
    
    // Validar que todos los campos estén completos
    const errors = {};
    if (!firstName.trim()) errors.firstName = t.errorFirstNameRequired || 'El nombre es requerido';
    if (!lastName.trim()) errors.lastName = t.errorLastNameRequired || 'El apellido es requerido';
    if (!email.trim()) errors.email = t.errorEmailRequired || 'El email es requerido';
    if (!message.trim()) errors.message = t.errorMessageRequired || 'El mensaje es requerido';
    
    // Si hay errores, mostrarlos y detener el envío
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
      // Limpiar todos los errores previos
    setFormErrors({});
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '238b7817-5841-489a-b315-7a13d4de224c',
          name: `${firstName} ${lastName}`,
          email: email,
          message: message,
          subject: `Nuevo mensaje de portfolio de ${firstName} ${lastName}`
        })
      });
        if (response.ok) {
        setSubmitMessage(t.messageSent || 'Mensaje enviado correctamente');
        // Resetear el formulario y los errores
        setFormData({ firstName: '', lastName: '', email: '', message: '' });
        setFormErrors({});
      } else {
        setSubmitMessage(t.messageError || 'Error al enviar el mensaje');
      }
    } catch (error) {
      setSubmitMessage(t.messageError || 'Error al enviar el mensaje');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (    <div 
      style={{ fontFamily: "Raleway, sans-serif" }} 
      className={`min-h-screen transition-colors duration-300 ${
        darkMode ? 'bg-gray-900 text-white' : 'bg-stone-200 text-gray-900'
      }`}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Raleway:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />
      
      {/* Fixed navbar */}
      <Navigation 
        isDarkMode={darkMode} 
        setIsDarkMode={setIsDarkMode} 
        language={currentLanguage} 
        setLanguage={setLanguage} 
      />
      
      {/* Main content with top padding to account for fixed navbar */}
      <main className="pt-16">{/* Added pt-16 to ensure content starts below the navbar */}
      <AnimatedSection animationType="fadeInUp">
        <section className={`pt-28 pb-20 px-12 transition-colors duration-300 ${
          darkMode ? 'bg-gray-800' : 'bg-stone-200'
        }`}>
          <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2
                className={`text-5xl font-semibold mb-8 transition-colors duration-300 ${
                  darkMode ? 'text-gray-100' : 'text-gray-700'
                }`}
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                {t.contact}
              </h2>
              <p className={`text-lg mb-8 transition-colors duration-300 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {t.contactDescription}
              </p>
              
              {/* Social Media Links */}
              <div className="flex gap-4 mt-8">
                <a
                  href="https://www.instagram.com/ine.vaz"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className={`w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform ${
                    darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'
                  }`}
                >
                  <FaInstagram className="text-2xl" />
                </a>
                <a
                  href="https://linkedin.com/in/inevaz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform ${
                    darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'
                  }`}
                >
                  <FaLinkedin className="text-2xl" />
                </a>
                <a
                  href="https://github.com/inevaz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-12 h-12 rounded-full flex items-center justify-center hover:scale-110 transition-transform ${
                    darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-800'
                  }`}
                >
                  <FaGithub className="text-2xl" />
                </a>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {submitMessage && (
                <div className={`p-4 rounded-lg text-center ${
                  submitMessage.includes('Error') || submitMessage.includes('error')
                    ? darkMode ? 'bg-red-900/20 text-red-400 border border-red-500/30' : 'bg-red-100 text-red-700 border border-red-300'
                    : darkMode ? 'bg-green-900/20 text-green-400 border border-green-500/30' : 'bg-green-100 text-green-700 border border-green-300'
                }`}>
                  {submitMessage}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">                <div>
                  <label 
                    htmlFor="firstName"
                    className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {t.firstName}
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    autoComplete="given-name"
                    className={`form-input w-full px-4 py-2 rounded-2xl border bg-transparent placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors duration-300 ${
                      darkMode 
                        ? 'border-gray-500 text-gray-200 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-gray-600 text-gray-800 focus:ring-red-900/20 focus:border-red-900'
                    } ${formErrors.firstName ? 'border-red-500' : ''}`}
                  />
                  {formErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>
                  )}
                </div>                <div>
                  <label
                    htmlFor="lastName" 
                    className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {t.lastName}
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    autoComplete="family-name"
                    className={`form-input w-full px-4 py-2 rounded-2xl border bg-transparent placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors duration-300 ${
                      darkMode 
                        ? 'border-gray-500 text-gray-200 focus:ring-red-500/20 focus:border-red-500'
                        : 'border-gray-600 text-gray-800 focus:ring-red-900/20 focus:border-red-900'
                    } ${formErrors.lastName ? 'border-red-500' : ''}`}
                  />
                  {formErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>
                  )}
                </div>
              </div>
                <div>
                <label
                  htmlFor="email"
                  className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {t.email}
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  className={`form-input w-full px-4 py-2 rounded-2xl border bg-transparent placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors duration-300 ${
                    darkMode 
                      ? 'border-gray-500 text-gray-200 focus:ring-red-500/20 focus:border-red-500'
                      : 'border-gray-600 text-gray-800 focus:ring-red-900/20 focus:border-red-900'
                  } ${formErrors.email ? 'border-red-500' : ''}`}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>
                <div>
                <label
                  htmlFor="message"
                  className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {t.message}
                </label>
                <textarea
                  id="message"
                  rows="5"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={`form-input w-full px-4 py-2 rounded-2xl border bg-transparent placeholder-gray-500 focus:outline-none focus:ring-2 resize-vertical transition-colors duration-300 ${
                    darkMode 
                      ? 'border-gray-500 text-gray-200 focus:ring-red-500/20 focus:border-red-500'
                      : 'border-gray-600 text-gray-800 focus:ring-red-900/20 focus:border-red-900'
                  } ${formErrors.message ? 'border-red-500' : ''}`}
                ></textarea>
                <div className="flex justify-between items-center mt-1">
                  <p className={`text-xs transition-colors duration-300 ${
                    darkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {formData.message ? formData.message.length : 0}/1000 caracteres
                  </p>
                  {formErrors.message && (
                    <p className="text-red-500 text-xs">{formErrors.message}</p>
                  )}
                </div>
              </div>
                <div>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode 
                      ? 'bg-red-600 text-white hover:bg-red-500 disabled:bg-red-800'
                      : 'bg-red-900 text-white hover:bg-red-800 disabled:bg-red-700'
                  }`}
                >
                  {isSubmitting ? (t.sending || 'Enviando...') : t.send}
                </button>
                
                {/* Mensaje de error general si hay campos incompletos */}
                {Object.keys(formErrors).length > 0 && (
                  <p className="text-red-500 text-xs mt-2 text-center">
                    {t.errorFormValidation || 'Por favor completa todos los campos requeridos'}
                  </p>
                )}
              </div>
            </form>
          </div>
        </section>      </AnimatedSection>
      </main>
      
      <Footer isDarkMode={darkMode} language={currentLanguage} />
    </div>
  );
};

export default Contacto;
